import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPromise } from 'fingerprintjs2';
import styled from 'styled-components';
import classNames from 'classnames';
import {
	getReferenceFromRefereeToken,
	getCandidateFromRefereeToken,
	sendEvent,
	getReferenceFormSpecification,
	updateCapturedReferenceDetailsAsReferee,
	completeReferenceAsReferee,
	setRefereeConsent
} from '../../../core/api';
import Loading from '../../../commonComponents/Loading';
import SecondaryButton from '../../common/buttons/SecondaryButton';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import Reference, { ChildReference } from '../../../models/be/Reference';
import RefereePageLayout from '../../layouts/RefereePageLayout';
import PrimaryName from '../../../models/be/PrimaryName';
import { getLocaleDate, getDateInDayMonthYearFormat, hasValue, getUniqueQueryString } from '../../../lib/utils';
import { RefereeEvent } from '../../../models/be/Event';
import { EventTypes } from '../../../core/enums';
import ReferenceForm from '../CandidateReferences/ReferenceForm';
import { Section } from '../../../models/be/db/FormSpecification';
import Document from '../../../models/be/db/Document';
import MessageBox from '../../common/MessageBox';
import ReferenceReplacementDocument from '../../common/ReferenceReplacementDocument';
import RequesterNotesForReferee from '../../common/RequesterNotesForReferee';
import './RefereeValidation.less';
import { pref, refereeRole } from '../../../core/constants';
import { getRefereeTokenFromQueryOrLocalStorage } from '../../../core/domain';
import { statuses } from '../../../lib/ApiBase';
import { Session } from '../../../models/be/transport/Session';
import RefereeExpiryPage from '../../common/RefereeExpiryPage';
import { SaveAndFinishLater } from '../refereeSaved/RefereeSaved';
import { clearRefereeTokenInLocalStorage, getAccessToken, hasSystemAdminRole } from '../../../core/session';
import TermsLink from '../../common/TermsLink';
import Buttons from '../../common/Buttons';
import { useAsyncAction } from '../../../commonComponents/Hooks';
import { getReferenceTemplateType } from '../../../models/be/ReferenceTemplate';
import DigitalSignatureField from '../../../commonComponents/fields/DigitalSignatureField';
import { SignatureWrapper } from '../../common/dataCapture/DataCapturePage';
import ConsentSignature from '../../../models/be/ConsentSignature';
import Workflow from '../../../models/be/Workflow';
import YesNoDialog from '../../common/Dialogs/YesNoDialog';
import RadioButtons from '../../common/buttons/RadioButtons';
import AdditionalName from '../../../models/be/AdditionalName';

const formatSingleName = (name: AdditionalName): string => {
	const firstName = name.firstName?.trim() || '';
	const lastName = name.lastName?.trim() || '';

	if (firstName && lastName) {
		return `${firstName} ${lastName}`;
	}
	return firstName || lastName || '';
};

export const formatAdditionalNamesForRefereeEmail = (additionalNames: AdditionalName[] | null | undefined): string => {
	if (!additionalNames || additionalNames.length === 0) {
		return '';
	}

	const validNames = additionalNames
		.filter((name): name is AdditionalName => name !== null && name !== undefined)
		.filter(
			(name) => (name.firstName && name.firstName.trim() !== '') || (name.lastName && name.lastName.trim() !== '')
		);

	if (validNames.length === 0) {
		return '';
	}

	const formattedNames = validNames.map(formatSingleName).filter((name) => name.trim() !== '');

	if (formattedNames.length === 0) {
		return '';
	}

	const joinedNames = formattedNames.join(', ');
	return ` (previously known as ${joinedNames})`;
};

const RefereeValidation = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [refereeSelection, setRefereeSelection] = React.useState<'canProvide' | 'canConfirm' | ''>('');
	const [disableConfirm, setDisableConfirm] = React.useState<boolean>(false);
	const [loading, errorMessage, performAsyncAction] = useAsyncAction(true);
	const [refereeToken, setRefereeToken] = React.useState<string>();
	const [childReference, setChildReference] = React.useState<ChildReference>();
	const [workflow, setWorkflow] = React.useState<Workflow>();
	const [candidatePrimaryName, setCandidatePrimaryName] = React.useState<PrimaryName>();
	const [referenceFormSections, setReferenceFormSections] = React.useState<Section[]>();
	const [showExpiryPage, setShowExpiryPage] = React.useState(false);
	const [showReferenceForm, setShowReferenceForm] = React.useState(false);
	const [showRefereeDocumentUpload, setShowRefereeDocumentUpload] = React.useState(false);
	const [session, setSession] = React.useState<Session>();
	const [showDialog, setShowDialog] = React.useState(false);
	const systemAdminToken = hasSystemAdminRole() ? getAccessToken() : null;
	const action = getUniqueQueryString(location.search, 'action');

	React.useEffect(() => {
		// TODO: Once all pages are updated to new css, should update css for body and remove following line.
		// Add following line to reuse the css.
		document.body.classList.add('dataCapturePage');

		performAsyncAction(async () => {
			const [newRefereeToken, tokenIsValid] = await getRefereeTokenFromQueryOrLocalStorage(location);
			if (tokenIsValid) {
				try {
					const [newReference, newCandidatePrimaryName, newReferenceFormSections] = await Promise.all([
						getReferenceFromRefereeToken(newRefereeToken),
						getCandidateFromRefereeToken(newRefereeToken),
						getReferenceFormSpecification(newRefereeToken)
					]);

					setChildReference(newReference.childReference);
					setWorkflow(newReference.workflow);
					setCandidatePrimaryName(newCandidatePrimaryName);
					setReferenceFormSections(newReferenceFormSections);
					// getPromise returns a full browser fingerprint based on the users browser, we obfuscate it by encoding it as base64 before transporting it to the API
					setSession({ item1: btoa(JSON.stringify(await getPromise())) });
					const refereePreSelectedOption = getRefereeAction(newReference.workflow);
					setRefereeSelection(refereePreSelectedOption);
					setDisableConfirm(newReference.workflow?.referenceWorkflow?.requiresRefereeAnswers);
					setRefereeToken(newRefereeToken);
				} catch (e) {
					if (e.status === statuses.unauthorized) {
						setShowExpiryPage(true);
					} else {
						throw e;
					}
				}
			} else {
				setShowExpiryPage(true);
			}
		});

		return () => {
			document.body.classList.remove('dataCapturePage');
		};
	}, []);

	const getRefereeAction = (updatedWorkflow: Workflow) => {
		if (updatedWorkflow?.referenceWorkflow?.requiresRefereeAnswers) return 'canProvide';
		if (action === 'confirm') return 'canConfirm';
		return 'canProvide';
	};

	const handleNextButtonClick = async () => {
		await performAsyncAction(async () => {
			const event: RefereeEvent = {
				type: EventTypes.RefereeConfirmedInformation,
				refereeToken,
				systemAdminToken
			};
			await sendEvent(event);
			await setRefereeConsent(refereeToken, true);
			if (
				childReference.refereeAnswersRequired === false ||
				action === 'confirm' ||
				(!workflow?.referenceWorkflow?.requiresRefereeAnswers && refereeSelection === 'canConfirm')
			) {
				await completeReferenceAsReferee(
					refereeToken,
					false,
					session,
					action === 'confirm' || !workflow?.referenceWorkflow?.requiresRefereeAnswers
				);
				clearRefereeTokenInLocalStorage();
				navigate('/referee/thanks');
			} else navigate('/referee/questions');
		});
	};

	const handleYes = async () => {
		await performAsyncAction(async () => {
			await setRefereeConsent(refereeToken, false);
			navigate('/referee/declined');
		});
		setShowDialog(false);
	};

	const handleEditButtonClick = async () => {
		setShowReferenceForm(true);
	};

	const handleFormBackButtonClick = async () => {
		setShowReferenceForm(false);
	};

	const handleFormSaveButtonClick = async (reference: Reference) => {
		await performAsyncAction(async () => {
			await updateCapturedReferenceDetailsAsReferee(refereeToken, reference.capturedReferenceDetails);
			setShowReferenceForm(false);
			setChildReference({ ...childReference, capturedReferenceDetails: reference.capturedReferenceDetails });
		});
	};

	const handleUploadDocumentClick = () => {
		setShowRefereeDocumentUpload(true);
		return false;
	};

	const hideReferenceReplacementDocument = async (document: Document) => {
		setShowRefereeDocumentUpload(false);
		setChildReference({ ...childReference, referenceReplacementDocument: document });
	};

	const handleNavigateToPage = async (pageSlug: string) => {
		navigate(`/referee/${pageSlug}`);
	};

	if (hasValue(errorMessage)) return <MessageBox error={errorMessage} />;

	if (showExpiryPage) {
		return <RefereeExpiryPage expiredRefereeToken={refereeToken} />;
	}

	if (loading) return <Loading />;

	// This has to be after showExpiryPage and loading.
	if (location.search.indexOf('refereeToken') !== -1) {
		navigate(location.pathname, { replace: true });
		return null;
	}

	const reference = childReference;
	const refereeDetails = reference?.capturedReferenceDetails;
	const fromDate = getDateInDayMonthYearFormat(getLocaleDate(refereeDetails?.fromDate));
	const toDate = getDateInDayMonthYearFormat(getLocaleDate(refereeDetails?.toDate));
	const dob = getDateInDayMonthYearFormat(getLocaleDate(refereeDetails?.dateOfBirth));
	const consentSignature: ConsentSignature = {
		consentTime: workflow.consent?.signedTimestamp,
		imageUrl: workflow.consent?.signature,
		ipAddress: workflow.consent?.ipAddress
	};
	let organisationLabel = null;
	switch (reference?.workflowTypeCode) {
		case 'EDREF':
			organisationLabel = 'School/College/University';
			break;
		case 'AREF':
			organisationLabel = 'Agency Name';
			break;
		case 'SEREF':
			organisationLabel = 'Business Name';
			break;
		default:
			organisationLabel = 'Organisation Name';
	}

	let locationLabel = null;
	switch (reference?.workflowTypeCode) {
		case 'EDREF':
			locationLabel = 'Establishment Address';
			break;
		case 'AREF':
			locationLabel = 'Agency Address';
			break;
		default:
			locationLabel = 'Organisation Location';
	}

	return (
		<>
			{showDialog && (
				<YesNoDialog
					heading="Decline referee"
					message="Are you sure you want to decline being a referee?"
					onYes={handleYes}
					onNo={() => setShowDialog(false)}
					yesButtonText="Yes"
					noButtonText="No"
					yesButtonIsDelete
				/>
			)}
			<RefereePageLayout
				navigateToPage={handleNavigateToPage}
				className="refereeValidationPage"
				title="Validation"
				currentFlowProgressBarName="Validation"
				refereeQuestionsRequired={childReference?.refereeAnswersRequired}
			>
				{childReference && (
					<>
						{/* eslint-disable-next-line no-nested-ternary */}
						{showReferenceForm ? (
							<ReferenceForm
								reference={childReference}
								formSections={referenceFormSections}
								onBackButtonClick={handleFormBackButtonClick}
								onSaveButtonClick={handleFormSaveButtonClick}
								userRole={refereeRole}
							/>
						) : showRefereeDocumentUpload ? (
							<ReferenceReplacementDocument
								refereeToken={refereeToken}
								reference={childReference}
								onBackButtonClick={hideReferenceReplacementDocument}
							/>
						) : (
							<>
								<p className="pageIntroduction">
									<b>
										Please ensure the dates and details below match what you have on file for the candidate. If they are
										different, click the Edit button to change the data, then click Save.
									</b>{' '}
									If you prefer, you can {/* TODO Replace with button */}
									{/* eslint-disable-next-line no-script-url */}
									<a id="uploadDocumentLink" onClick={handleUploadDocumentClick}>
										upload a document
									</a>{' '}
									instead which validates the requested information.
								</p>
								<RequesterNotesForReferee
									notes={childReference?.notesToReferee}
									consentToNotes={childReference?.refereeNotesAccept}
								/>
								<h3>{getReferenceTemplateType(reference)}</h3>
								<section className="borderContent">
									<p>REFERENCE TO</p>
									<ul>
										<li>
											<span className="bold">
												{candidatePrimaryName.firstName} {candidatePrimaryName.lastName}
												{formatAdditionalNamesForRefereeEmail(workflow.additionalNames)}
											</span>
										</li>
										{refereeDetails.candidateJobTitle && (
											<li>
												Job Title: <span className="bold">{refereeDetails.candidateJobTitle}</span>
											</li>
										)}
										{refereeDetails.dateOfBirth && (
											<li>
												DOB: <span className="bold">{dob}</span>
											</li>
										)}
										{refereeDetails.nationalInsuranceNumber && (
											<li>
												N.I Number: <span className="bold">{refereeDetails.nationalInsuranceNumber}</span>
											</li>
										)}

										{/* Self Employed */}
										{refereeDetails.selfEmployment.natureOfBusiness && (
											<li>
												Nature of Business:{' '}
												<span className="bold">{refereeDetails.selfEmployment.natureOfBusiness}</span>
											</li>
										)}
										{refereeDetails.selfEmployment.businessName && (
											<li>
												Business Name: <span className="bold">{refereeDetails.selfEmployment.businessName}</span>
											</li>
										)}
										{refereeDetails.selfEmployment.companyRegistrationNumber && (
											<li>
												Company Registration Number:{' '}
												<span className="bold">{refereeDetails.selfEmployment.companyRegistrationNumber}</span>
											</li>
										)}
										{refereeDetails.selfEmployment.businessType && (
											<li>
												Business Type: <span className="bold">{refereeDetails.selfEmployment.businessType}</span>
											</li>
										)}
										{refereeDetails.selfEmployment.reasonForCeasingTrade && (
											<li>
												Reason for ceasing trade:{' '}
												<span className="bold">{refereeDetails.selfEmployment.reasonForCeasingTrade}</span>
											</li>
										)}

										{/* Education */}
										{refereeDetails.courseName && (
											<li>
												Course Attended: <span className="bold">{refereeDetails.courseName}</span>
											</li>
										)}
										{refereeDetails.qualificationName && (
											<li>
												Qualification Name: <span className="bold">{refereeDetails.qualificationName}</span>
											</li>
										)}
										{refereeDetails.qualificationGrade && (
											<li>
												Qualification Grade: <span className="bold">{refereeDetails.qualificationGrade}</span>
											</li>
										)}
										{hasValue(refereeDetails.qualificationAchieved) && (
											<li>
												Qualification Achieved:{' '}
												<span className="bold">{refereeDetails.qualificationAchieved ? 'Yes' : 'No'}</span>
											</li>
										)}

										<li>
											From <span className="bold">{fromDate}</span> to <span className="bold">{toDate}</span>
										</li>
									</ul>
									<p>REFERENCE FROM</p>
									<ul>
										<li>
											<span className="bold">
												{refereeDetails.firstName} {refereeDetails.lastName}
											</span>
										</li>

										{/* Employment */}
										{refereeDetails.refereeJobTitle && (
											<li>
												{' '}
												Job Title: <span className="bold">{refereeDetails.refereeJobTitle}</span>
											</li>
										)}

										{/* Personal */}
										{refereeDetails.relationshipToCandidate && (
											<li>
												Relationship to You: <span className="bold">{refereeDetails.relationshipToCandidate}</span>
											</li>
										)}

										{refereeDetails.organisationName && (
											<li>
												{organisationLabel}: <span className="bold">{refereeDetails.organisationName}</span>
											</li>
										)}
										{refereeDetails.organisationAddress && (
											<li>
												{locationLabel}: <span className="bold">{refereeDetails.organisationAddress}</span>
											</li>
										)}
									</ul>
									<SecondaryButton id="editReferenceButton" text="Edit" onClick={handleEditButtonClick} />
								</section>
								<h3>Candidate Consent Given</h3>
								{reference?.workflowTypeCode !== pref && (
									<SignatureWrapper>
										<div className="sectionContainer">
											<ConsentContainer>
												I provide my consent for VETTING.com to obtain a reference (personal, professional, employment
												or education) as appropriate. I acknowledge and provide my consent to the sharing of my personal
												data with the referees I nominate so that they can identify me and see that I have given my
												consent to VETTING.com . I understand and consent to sharing my personal details, National
												Insurance number and digital signature with my chosen referees for the purpose of this screening
												process.
											</ConsentContainer>
											<DigitalSignatureField
												email={workflow.candidateEmail}
												workflowId={workflow.id}
												consentSignature={consentSignature}
												candidateId={workflow.candidateId}
												customerId={workflow.clientCustomerId}
												showButtons={false}
												token={refereeToken}
											/>
										</div>
									</SignatureWrapper>
								)}
								<CheckboxContainer>
									<PaddedFieldset>
										<div className={classNames('borderContent', 'greyStyle')}>
											<RadioButtons
												options={[
													{
														text: 'I can provide a reference',
														value: 'canProvide'
													},
													{
														text: 'I can only confirm the details above',
														value: 'canConfirm',
														disabled: disableConfirm
													}
												]}
												currentValue={refereeSelection}
												onClick={(value) => setRefereeSelection(value as 'canProvide' | 'canConfirm')}
												asOneButton={false}
											/>
										</div>
									</PaddedFieldset>
								</CheckboxContainer>
								<p>
									We collect some data during this process but rest assured your data is provided in confidence and it
									will not be shared or used for marketing purposes. By proceeding, you agree to provide a reference
									(review <TermsLink text="terms" /> if you would like).
								</p>
								<Buttons>
									<SecondaryButton
										className="refereeDeclineButton"
										text="I Decline"
										onClick={() => setShowDialog(true)}
									/>
									<PrimaryButton text="The Information is Right" onClick={handleNextButtonClick} />
								</Buttons>
								<SaveAndFinishLater refereeToken={refereeToken} currentPagePath={location.pathname} />
							</>
						)}
					</>
				)}
			</RefereePageLayout>
		</>
	);
};

export default RefereeValidation;

const ConsentContainer = styled.div`
	padding-bottom: 10px;
`;

const CheckboxContainer = styled.div`
	font-size: 15px;
	width: 100%;
	margin-top: 10px;
	color: rgb(84, 105, 141);
	input {
		margin-top: -2px;
	}
`;

const PaddedFieldset = styled.div`
	 {
		padding: 10px 0 20px 0;
		.label {
			padding: 10px 0 20px 0 !important;
			position: relative;
			top: 3px;
		}
	}
`;
